/**
 * Vue Router matches paths case-insensitively, so /Blog or /PRACA render the real
 * page — with a self-referencing canonical, which hands search engines a duplicate.
 * Redirect (301) such requests to the route's own spelling of its static segments.
 *
 * Only static segments are rewritten. Dynamic ones are slugs or Firestore ids and
 * are case-sensitive (/orgs/MEDCON, /dobrovolnictvo/dobrovolnicke-projekty/3Q6tBLsiL33oeQBsNksv),
 * so they and the query string are copied byte for byte from the raw request URL
 * (the router's `to.fullPath` re-encodes the query). The matched route record decides
 * which segment is which, so new pages need no configuration.
 *
 * Server only: crawlers and pasted links arrive as full requests, and client-side
 * navigation keeps whatever the app links to.
 */
export default defineNuxtRouteMiddleware((to) => {
    if (!import.meta.server) return

    const record = to.matched[to.matched.length - 1]
    // No real route matched, or a catch-all: there is no static spelling to restore.
    if (!record || /[*+?]|\(\.\*\)/.test(record.path)) return

    const rawUrl = useRequestEvent()?.path
    if (!rawUrl) return
    const queryStart = rawUrl.indexOf('?')
    const rawPath = queryStart === -1 ? rawUrl : rawUrl.slice(0, queryStart)
    if (!/[A-Z]/.test(rawPath) || !isSamePath(rawPath, to.path)) return

    const trailingSlash = rawPath.length > 1 && rawPath.endsWith('/')
    const recordSegments = record.path.split('/')
    const pathSegments = (trailingSlash ? rawPath.slice(0, -1) : rawPath).split('/')
    if (recordSegments.length !== pathSegments.length) return

    const normalizedSegments: string[] = []
    for (const [index, segment] of pathSegments.entries()) {
        const recordSegment = recordSegments[index]!
        if (recordSegment.includes(':')) {
            normalizedSegments.push(segment)
        } else if (segment.toLowerCase() === recordSegment.toLowerCase()) {
            normalizedSegments.push(recordSegment)
        } else {
            // Encoded characters or anything unexpected: not ours to rewrite.
            return
        }
    }

    const path = normalizedSegments.join('/') + (trailingSlash ? '/' : '')
    if (path === rawPath) return

    return navigateTo(path + (queryStart === -1 ? '' : rawUrl.slice(queryStart)), { redirectCode: 301 })
})

/**
 * Whether the router is still on the requested path. After the redirect below, the
 * server-side router moves on to the corrected path while the raw request URL stays
 * the same — without this check the middleware would redirect forever.
 */
function isSamePath(rawPath: string, routePath: string): boolean {
    try {
        return decodeURI(rawPath) === decodeURI(routePath)
    } catch {
        return false
    }
}
